import { useEffect, useState, useMemo } from "react";
import { Row, Container, Button, Col, Popover, OverlayTrigger } from "react-bootstrap";
import { useSelector } from "react-redux";
import ReactDataGrid from '@inovua/reactdatagrid-community'
import NumberFilter from '@inovua/reactdatagrid-community/NumberFilter'
import '@inovua/reactdatagrid-community/index.css'
import EditModal from "./EditModal";
import RemoveModal from "./RemoveModal";

import toastService from "../../_services/toastService";
import SatAPI from "../../_services/sat";
import { calculateRoundedProgrammeVTEPreFetched, calculateAvailableFTEPreFetched, loadParameters } from "../../_helpers/calculations";
import { exportToExcel } from "../../_helpers/excel";
import { sortProgrammesLikeOpleidingenPage } from "../../_helpers/sortProgrammes";

const api = SatAPI();

function Programmes() {
  const snapshot = useSelector(state => state.snapshot.fileName);

  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [removeModal, setRemoveModal] = useState(null);
  const [gridRef, setGridRef] = useState(null);

  const loadProgrammes = async () => {
    try {
      setLoading(true);
      const response = await api.getProgrammesWithFTE(snapshot);
      response.sort((a, b) => a.name.localeCompare(b.name));
      /*
            const collegas = await api.getCollegas(snapshot);
            const assignments = await api.getAssignments(snapshot);
            const taskAssignments = await api.getTaskAssignments(snapshot);
            const { general, cs, ects } = await loadParameters();
      
            const oposResponse = await api.getOPOs(snapshot);
            oposResponse.sort((a, b) => b.name < a.name ? 1 : -1);
            let currentParallels = [];
            if (!snapshot) { // TEMPORARY WORKAROUND - old back-end always returns current parallels instead of parallels from snapshot
              currentParallels = await api.getParallels(snapshot);
              currentParallels.sort((a, b) => (b.mainOLA.name.localeCompare(a.mainOLA.name) || a.level - b.level) ? 1 : -1);
            }
      
            const allOLAsFromOPOS = [];
            oposResponse.forEach(opo => {
              opo.olas.forEach(ola => {
                allOLAsFromOPOS.push(ola);
              });
            });
      
            // TODO: improve this in new back-end!
            // Overwrite the OLA's in parallel because they don't contain OLA phases,
            // which is necessary for calculation of student numbers
            currentParallels.forEach(parallel => {
              parallel.mainOLA = allOLAsFromOPOS.find((ola) => ola.ID === parallel.mainOLAID);
              parallel.subOLA = allOLAsFromOPOS.find((ola) => ola.ID === parallel.subOLAID);
            });
      */
      setProgrammes(response);

      setLoading(false);
    } catch (error) {
      toastService.send({ title: "An error occured", message: `${error}` });
    }
  };

  const openEditModal = programme => {
    setEditModal(
      <EditModal
        hideModal={() => {
          setEditModal(null);
          loadProgrammes();
        }}
        data={programme}
      />,
    );
  };

  const openRemoveModal = programme => {
    setRemoveModal(
      <RemoveModal
        hideModal={() => {
          setRemoveModal(null);
          loadProgrammes();
        }}
        data={programme}
      />,
    );
  };

  const saveExcel = async () => {
    const exportData = [];
    programmes.forEach((programme) => {
      exportData.push({
        'Naam': programme.name,
        'Factor forfait': programme.forfaitFactor,
      });
    });

    exportToExcel(exportData, 'Opleidingen');
  }

  const gridStyle = { minHeight: 550 }

  const fmt = (value) => value != null ? parseFloat(value).toFixed(2) : '';

  const popover = () => (
    <Popover style={{ maxWidth: 420 }}>
      <Popover.Header as="h3">Legende</Popover.Header>
      <Popover.Body>
        <p>
          <span style={{ display: 'inline-block', width: 16, height: 16, background: '#e9ecef', border: '1px solid #ced4da', marginRight: 8, verticalAlign: 'middle' }} />
          Grijze rij = clusteropleiding die opleidingen groepeert.
        </p>
        <hr />
        <p>
          <i className="fas fa-info-circle text-info me-2" />
          Telkens ongewogen stp uit model, maar dan de formule in kolom R aangepast naar bvb <code>=SUM(H45:P45)/3</code>. Idem bij GRAD.
        </p>
        <p><strong>PBA:</strong> kolom R</p>
        <p><strong>GRAD:</strong> kolom O</p>
        <p>
          De aanpassing van de formule is noodzakelijk om de werkstudenten en internationale studenten mee te nemen.
        </p>
      </Popover.Body>
    </Popover>
  );

  const groupedProgrammes = useMemo(
    () => sortProgrammesLikeOpleidingenPage(programmes).map(p => ({
      ...p,
      isClusterRow: !!p.isCluster,
      isChild: !p.isCluster && !!p.clusterProgrammeId,
    })),
    [programmes]
  );

  const columns = useMemo(() => [
    {
      name: 'name', header: 'Naam', defaultFlex: 1, render: ({ value, data }) => {
        if (data.isClusterRow) return <strong>{value}</strong>;
        if (data.isChild) return <span className="cluster-child-name">{value}</span>;
        return <span>{value}</span>;
      }
    },
    { name: 'credit', header: 'Studiepunten', type: 'number', defaultFlex: 1, filterEditor: NumberFilter, textAlign: 'end', render: ({ value }) => value ? Math.round(value).toLocaleString('en-US') : '' },
    { name: 'forfaitFactor', header: 'Factor forfait', type: 'number', defaultFlex: 1, render: ({ value }) => `100% = ${value} uur`, filterEditor: NumberFilter },
    {
      name: 'ID', header: 'Acties', defaultFlex: 1, draggable: false, sortable: false, render: ({ data }) => <div>
        <Button variant="info" onClick={() => openEditModal(data)} disabled={snapshot}>
          <i className="far fa-edit" />
        </Button>
        <Button variant="danger" onClick={() => openRemoveModal(data)} disabled={snapshot}>
          <i className="far fa-trash" />
        </Button></div>
    }
  ], [snapshot]);

  const filterValue = [
    { name: 'name', operator: 'contains', type: 'string', value: '' },
    { name: 'forfaitFactor', operator: 'inrange', type: 'number', value: 0 },
  ];

  useEffect(() => {
    loadProgrammes();
  }, [snapshot]);

  if (loading) {
    return (
      <div className="text-center">
        <i className="fad fa-spinner-third fa-spin fa-5x" />
      </div>
    );
  }
  return (
    <Container>
      {editModal}
      {removeModal}
      <Row>
        <h1>Opleidingen <i className="fas fa-info-circle text-info" style={{ fontSize: '0.5em' }} /></h1>
      </Row>
      <Row className="my-2">
        <Col className="text-end">
          <Button variant="secondary" onClick={() => saveExcel()}>
            <i className="far fa-file-export" /> Exporteren
          </Button>{" "}
          <Button variant="success" onClick={() => openEditModal()} disabled={snapshot}>
            <i className="far fa-plus" />
            &nbsp;Nieuw
          </Button>{" "}
          <OverlayTrigger trigger="click" placement="left" overlay={popover()} rootClose>
            <Button variant="info">
              <i className="fas fa-question" />
            </Button>
          </OverlayTrigger>
        </Col>
      </Row>
      <Row>
        <Col>
          <ReactDataGrid
            handle={setGridRef}
            idProperty="ID"
            style={gridStyle}
            defaultFilterValue={filterValue}
            columns={columns}
            dataSource={groupedProgrammes}
            rowStyle={({ data }) => data.isClusterRow ? { background: '#e9ecef' } : { background: '#fff' }}
          />
        </Col>
      </Row>
    </Container>
  );
}

export default Programmes;
